import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExternalLink, Mail, Receipt, Users } from "lucide-react";
import { formatJamaicaDate, formatJamaicaDateTime } from "@/lib/datetime";
import {
  formatCount, formatMoney, getCustomer, metricsUrls,
  type CustomerDetail, type MoneyRow,
} from "@/lib/metrics";
import { EmptyState, ErrorState, StatusPill, TableSkeleton, Tile, timelineStyle } from "./shared";

function rowDate(r: MoneyRow): string | null {
  return (r.at || r.created || r.date || null) as string | null;
}

function rowAmount(r: MoneyRow): number | null {
  const v = r.amount ?? r.amountPaid ?? r.amountDue ?? r.total;
  return typeof v === "number" ? v : null;
}

function rowLink(r: MoneyRow): string | null {
  return (r.hostedInvoiceUrl || r.url || r.receiptUrl || r.invoicePdf || null) as string | null;
}

function displayName(detail: CustomerDetail): string {
  const u = detail.user;
  if (!u) return detail.customerId;
  const joined = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return (u.name as string) || joined || (u.email as string) || detail.customerId;
}

function MoneyTable({ rows, emptyTitle, emptyHint, showReason }: {
  rows: MoneyRow[];
  emptyTitle: string;
  emptyHint: string;
  showReason?: boolean;
}) {
  if (!rows || rows.length === 0) return <EmptyState title={emptyTitle} hint={emptyHint} />;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            {showReason ? <TableHead>Reason</TableHead> : null}
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => {
            const link = rowLink(r);
            return (
              <TableRow key={(r.id as string) || i}>
                <TableCell className="whitespace-nowrap text-sm">{formatJamaicaDate(rowDate(r))}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.currency && String(r.currency).toLowerCase() !== "jmd"
                    ? `${String(r.currency).toUpperCase()} ${((rowAmount(r) ?? 0) / 100).toFixed(2)}`
                    : formatMoney(rowAmount(r))}
                </TableCell>
                <TableCell><StatusPill status={r.status || null} /></TableCell>
                {showReason ? (
                  <TableCell className="text-sm text-muted-foreground max-w-[14rem] truncate">
                    {r.reason || r.failureMessage || r.failureCode || r.description || "–"}
                  </TableCell>
                ) : null}
                <TableCell className="text-right">
                  {link ? (
                    <a href={link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CustomerProfileSheet({
  customerId, onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery<CustomerDetail>({
    queryKey: [customerId ? metricsUrls.customer(customerId) : "metrics-customer-idle"],
    queryFn: () => getCustomer(customerId as string),
    enabled: !!customerId,
  });

  const subs = data?.subscriptions || [];
  const primary = subs.find((s) => s.status === "active" || s.status === "trialing") || subs[0];
  const since = subs
    .map((s) => (s.startedAt || s.createdAt) as string | null)
    .filter(Boolean)
    .sort()[0] || null;
  const userId = (data?.user?.id as string | undefined) || undefined;

  return (
    <Sheet open={!!customerId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="sheet-customer-profile">
        {isLoading ? (
          <div className="pt-6"><TableSkeleton rows={8} cols={4} /></div>
        ) : error ? (
          <div className="pt-6"><ErrorState error={error} /></div>
        ) : !data ? null : (
          <div className="space-y-5">
            <SheetHeader className="space-y-1">
              <SheetTitle className="flex flex-wrap items-center gap-2">
                {displayName(data)}
                {data.user?.grade != null && <Badge variant="outline">Grade {data.user.grade}</Badge>}
                <StatusPill status={(primary?.status as string) || null} />
              </SheetTitle>
              <SheetDescription>
                {data.user?.email || data.customerId}
                {primary?.plan ? ` · ${primary.plan}${primary.interval ? ` / ${primary.interval}` : ""}` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <Tile label="MRR" value={formatMoney(data.mrr)} />
              <Tile label="Lifetime value" value={formatMoney(data.ltv)} />
              <Tile label="Customer since" value={since ? formatJamaicaDate(since) : "–"} sub={`${formatCount(data.stats.months)} months`} />
              <Tile
                label="Payments"
                value={formatCount(data.stats.payments)}
                sub={`${formatCount(data.stats.failed)} failed · ${formatMoney(data.stats.refunded)} refunded`}
                tone={data.stats.failed > 0 ? "warn" : undefined}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {userId && (
                <>
                  <Button variant="outline" size="sm" asChild>
                    <Link href={`/admin/users?openUser=${userId}`}><Users className="w-4 h-4 mr-1" />Open user record</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link href={`/admin/comms?tab=contacts&user=${userId}`}><Mail className="w-4 h-4 mr-1" />Comms history</Link>
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" asChild>
                <a href={`https://dashboard.stripe.com/customers/${data.customerId}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" />Open in Stripe
                </a>
              </Button>
            </div>

            {/* Current subscriptions */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Current subscriptions</h3>
              {subs.length === 0 ? (
                <EmptyState title="No subscriptions on file" hint="This Stripe customer has never carried a subscription, or the mirror has not seen one yet." />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">MRR</TableHead>
                        <TableHead>Renews</TableHead>
                        <TableHead className="w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subs.map((s, i) => {
                        const id = (s.subscriptionId || s.id) as string | undefined;
                        return (
                          <TableRow key={id || i}>
                            <TableCell className="capitalize text-sm">
                              {s.plan || "–"}{s.interval ? <span className="text-xs text-muted-foreground"> / {s.interval}</span> : null}
                            </TableCell>
                            <TableCell>
                              <StatusPill status={(s.status as string) || null} />
                              {s.cancelAtPeriodEnd ? <Badge variant="outline" className="ml-1 text-[10px]">ending</Badge> : null}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{formatMoney((s.mrr ?? s.amount) as number | null)}</TableCell>
                            <TableCell className="whitespace-nowrap text-sm">{formatJamaicaDate((s.currentPeriodEnd || s.periodEnd) as string | null)}</TableCell>
                            <TableCell className="text-right">
                              {id ? (
                                <a href={`https://dashboard.stripe.com/subscriptions/${id}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Stripe</a>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              {data.currentMrrByPrice.length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  {data.currentMrrByPrice.map((p) => (
                    <Badge key={p.id} variant="outline" className="font-normal text-[11px]">
                      {p.plan || p.id} · {formatMoney(p.mrr)}/mo
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Timeline</h3>
              {data.timeline.length === 0 ? (
                <EmptyState title="Nothing has happened yet" hint="Payments, failures, refunds, trials and cancellations all land here once they are mirrored." />
              ) : (
                <ol className="space-y-3">
                  {data.timeline.map((entry, i) => {
                    const style = timelineStyle(entry.kind);
                    const Icon = style.icon;
                    return (
                      <li key={`${entry.at}-${i}`} className="flex gap-3" data-testid={`timeline-${entry.kind}-${i}`}>
                        <span className={`rounded-full p-1.5 h-7 w-7 flex items-center justify-center shrink-0 ${style.className}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <div className="min-w-0 flex-1 border-b pb-3">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="font-medium text-sm">{entry.title}</span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatJamaicaDateTime(entry.at)}</span>
                          </div>
                          {entry.detail ? <div className="text-xs text-muted-foreground">{entry.detail}</div> : null}
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            {entry.amount != null && (
                              <span className="text-xs tabular-nums font-medium">
                                {entry.currency && entry.currency.toLowerCase() !== "jmd"
                                  ? `${entry.currency.toUpperCase()} ${(entry.amount / 100).toFixed(2)}`
                                  : formatMoney(entry.amount)}
                              </span>
                            )}
                            {entry.status ? <Badge variant="outline" className="text-[10px] font-normal">{entry.status}</Badge> : null}
                            {entry.url ? (
                              <a href={entry.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                Open <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Money tabs */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Receipt className="w-4 h-4" />Billing records
              </h3>
              <Tabs defaultValue="invoices">
                <TabsList>
                  <TabsTrigger value="invoices">Invoices ({data.invoices.length})</TabsTrigger>
                  <TabsTrigger value="charges">Charges ({data.charges.length})</TabsTrigger>
                  <TabsTrigger value="refunds">Refunds ({data.refunds.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="invoices" className="mt-3">
                  <MoneyTable rows={data.invoices} emptyTitle="No invoices" emptyHint="Stripe has not issued an invoice for this customer yet." />
                </TabsContent>
                <TabsContent value="charges" className="mt-3">
                  <MoneyTable rows={data.charges} showReason emptyTitle="No charges" emptyHint="No card has been charged on this customer yet." />
                </TabsContent>
                <TabsContent value="refunds" className="mt-3">
                  <MoneyTable rows={data.refunds} showReason emptyTitle="No refunds" emptyHint="Nothing has been refunded to this customer." />
                </TabsContent>
              </Tabs>
            </div>

            {data.feedback.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Exit survey</h3>
                <ul className="space-y-2">
                  {data.feedback.map((f, i) => (
                    <li key={i} className="rounded-md border p-2 text-sm">
                      <div className="font-medium">{f.reason || "No reason given"}</div>
                      {f.details ? <div className="text-xs text-muted-foreground">{f.details}</div> : null}
                      {f.at ? <div className="text-xs text-muted-foreground mt-0.5">{formatJamaicaDate(f.at)}</div> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
